/**
 * @file Select.js
 * @project Web-panel
 * @author Pavel Shabardin (<bigbn@mail.ru>) Tuesday, 28th May 2019 4:17:52 pm
 * @copyright 2015 - 2019 SKAT LLC, Delive LLC
 * @flow
 */
import type { CombinedValue } from '../../../types'
import type { Props as BaseProps, State as BaseState } from './ReferenceSelect'

import * as React from 'react'
import ReferenceSelect from './ReferenceSelect'
import cn from 'classnames'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faAngleDown, faBackspace, faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import { KEY, CAST } from 'web-panel-essentials/misc'
import E from 'web-panel-essentials/errors'
import { __ } from '../../../globals'
import autoBind from 'react-autobind'

library.add(faCircleNotch, faAngleDown, faBackspace)

export type Props = BaseProps & {
}

export type State = BaseState & {
}

class Select<P: Props, S: State> extends ReferenceSelect<P, S> {
  handleClick: Function
  buffered: string
  bufferExpire: TimeoutID //eslint-disable-line

  constructor (props: any) {
    super(props)
    autoBind(this)
    this.state = {
      ...this.state,
      inputDisabled: true,
      referenceIcon: 'angle-down',
      placeholder: String(props.placeholder || __('SELECT')),
      role: 'select'
    }
    this.buffered = ''
    this.handleClick = this._handleClick.bind(this)
  }

  renderReferenceButton () : any {
    const { referenceIcon, pending } = this.state
    return (
      <div className={cn('field-button', { pending })} onClick={this.handleOpenReference}>
        <FontAwesomeIcon spin={pending} className='icon' icon={pending ? 'circle-notch' : referenceIcon} />
      </div>
    )
  }

  handleKeyPress (e: any) {
    const char = String.fromCharCode(e.charCode)
    this.buffered += char
    const items = this.state.items

    clearTimeout(this.bufferExpire)
    if (this.buffered && items.length) {
      const index = items.findIndex((item) => CAST.String(item[this.displayValueKey]).toLowerCase().startsWith(this.buffered.toLowerCase()))
      if (index !== -1) this.setState({ focusedItemIndex: index })
    }
    this.bufferExpire = setTimeout(() => { this.buffered = '' }, 500)
  }

  async _handleKeyDown (e: any) : Promise<void> {
    e.persist()
    if (e.keyCode === KEY.DOWN || e.keyCode === KEY.UP) {
      let { items } = this.state
      const { disabled, filter } = this.props
      if (disabled) return
      if (!items.length) {
        items = await this.props.dataProvider.get(filter)
        let focusedItemIndex = 0
        if (this.props.value) focusedItemIndex = items.findIndex((item) => item[this.valueKey] === this.props.value)
        await this.setState({ items, focusedItemIndex })
      }
    }
    super._handleKeyDown(e, true)
  }

  _handleClick () {
    this.focus()
    if (!this.state.items.length) this.handleOpenReference()
  }

  _renderReset () : React.Node {
    return null
  }

  getActiveSuggestion () : CombinedValue | null {
    return null
  }

  async handleOpenReference () : Promise<void> {
    const { disabled, filter } = this.props
    if (disabled) return
    if (!this.props.dataProvider) throw E('E_DDAT-E', 'DATA_PROVIDER_IS_REQURED')

    this.setState({ pending: true })
    let items = await this.props.dataProvider.get(filter)
    if (this.props.comparator) items = items.sort(this.props.comparator)
    let focusedItemIndex = 0
    if (this.props.value) focusedItemIndex = items.findIndex((item) => item[this.valueKey] === this.props.value)
    await this.setState({ items, focusedItemIndex, pending: false })
  }
}

export default Select
